import React, { useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useQuery } from 'react-query';
import highchartsStock from 'highcharts/modules/stock';
import highchartsExporting from 'highcharts/modules/exporting';
import Highcharts, { merge } from 'highcharts';
import { GetUserAssetResult, getAssetHistory } from 'config/api';
import { getDate } from 'components/features/utils/converter';
import { AssetBundleType } from 'components/features/home/Home';
import { ChartForPreviewProps } from 'components/features/preview-share/useChartRenderer';
import {
  UsePreviewProps,
  usePreview,
} from 'components/features/preview-share/Preview';
import {
  ChartPreviewState,
  StateController,
  defualtStateForHome,
  RenderedChartToElement,
  stateToOptions,
} from 'components/features/preview-share/PreviewUtils';
import { AreaChartLegend } from 'components/features/new-report/components/AssetTransitionChart/HighChart';
import Loading from 'components/commons/loading/Loading';
import { isIosApp } from 'components/features/utils/userStatus';
import {
  AssetStuffingChart,
  AssetStuffingChartInternal,
} from './AssetStuffingChart';
import { ColumnList, ServiceList } from './TotalAssetStuffingFluctuationList';
import { DividendItem } from './DividendItem';
import { usePreviewPager } from '../PortfolioStuffing/asset/PortfolioStuffingAssetChart';
import { updateRoutigList } from '../Tab';

highchartsStock(Highcharts);
highchartsExporting(Highcharts);

export type AssetStuffingChartDatum = {
  x: Date;
  y: number;
};

export type TimeSeriesDatum = {
  date: Date;
  value: number | boolean | string;
};

export type ExtraTimeSeriesData = {
  type: 'IncorrectSign';
  value: TimeSeriesDatum[];
};

export type TopixData = {
  topix: number;
  date: Date;
};

// 資産タブ
export type AssetStuffingChartProps = {
  data: AssetStuffingChartDatum[];
  extraData?: ExtraTimeSeriesData[];
};

export const AssetStuffingChartForPreview: (
  props: ChartForPreviewProps<AssetStuffingChartProps['data']>
) => React.ReactElement = ({
  data,
  options,
  range,
  setChartDataURL,
  topixData,
}) => {
  return (
    <>
      <AssetStuffingChartInternal
        {...{
          data,
          topixData,
          forPreviewSetter: { setChartDataURL, tag: 'forInPreview' },
          options: range
            ? merge(options, {
                xAxis: {
                  min: range.min.getTime(),
                  max: range.max.getTime(),
                },
                legend: {
                  useHTML: true,
                  itemDistance: -10,
                  margin: -10,
                  x: -5,
                  y: 0,
                  enabled: true,
                  align: 'left',
                  verticalAlign: 'top',
                  itemStyle: {
                    fontSize: '10px',
                    fontWeight: 'normal',
                    color: '#ffffff',
                  },
                  itemHoverStyle: { color: '#ffffff' },
                  className: 'override-highcharts-legend',
                  labelFormatter: function() {
                    if (this instanceof Highcharts.Series) {
                      const name = this.name;
                      const color = (() => {
                        const t = this as unknown;
                        if (
                          typeof t === 'object' &&
                          t &&
                          'color' in t &&
                          typeof (t as { color: unknown }).color === 'string'
                        ) {
                          return (t as { color: string }).color;
                        }
                        return '';
                      })();
                      if (this.type === 'area') {
                        return renderToStaticMarkup(
                          <AreaChartLegend color={color} lightOpacity={0.6}>
                            {name}
                          </AreaChartLegend>
                        );
                      }
                    }
                    return '';
                  },
                },
                plotOptions: {
                  series: {
                    lineWidth: 4,
                    turboThreshold: 15000,
                  },
                },
              } as Highcharts.Options)
            : options,
        }}
      />
    </>
  );
};

export const getTopixData = (assets: AssetHistoryItemsProps[]): TopixData[] => {
  const filterAssets = assets.filter(
    list => list.topix !== 0 && list.topix !== null
  );
  return filterAssets.map(asset => {
    return {
      topix: asset.topix ?? 0,
      date: getDate(asset.day),
    } as TopixData;
  });
};

export const assetsToChartData = (
  assets: AssetHistoryItemsProps[]
): AssetStuffingChartDatum[] => {
  return assets.map(asset => {
    return {
      x: getDate(asset.day),
      y: asset.totalAssetAmount ?? 0,
    };
  });
};

type AssetStuffingProps = {
  children: React.ReactElement;
  asset: GetUserAssetResult;
  assets: AssetHistoryItemsProps[];
  assetBundleType: AssetBundleType;
  UserBrokerUpdateTime: React.ReactElement;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

export type DataLoadingProps = {
  isFetching: boolean;
  isRefetching: boolean;
};

const AssetStuffingInternal = ({
  children,
  assets,
  asset,
  UserBrokerUpdateTime,
  toPreview,
  backHome,
  isFetching,
  isRefetching,
}: Omit<AssetStuffingProps, 'assetBundleType'> &
  DataLoadingProps): React.ReactElement => {
  const chartData = useMemo(() => assetsToChartData(assets), [assets]);
  const chartIncorrectData = useMemo(() => {
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: assets.map(it => {
        return {
          value: it.incorrect,
          date: getDate(it.day),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [assets]);

  const topixData = getTopixData(assets);
  const props: UsePreviewProps<ChartPreviewState, typeof chartData> = {
    Chart: AssetStuffingChartForPreview,
    defaultState: defualtStateForHome(asset),
    extra: chartData,
    topixExtra: topixData,
    RenderedChartToElement,
    stateToOptions,
    StateController,
    onCancel: backHome,
    chartTitle: '資産推移',
  };

  const { preview, TwitterIconPreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  useEffect(() => {
    updateRoutigList('asset');
  }, []);

  return (
    <>
      <TwitterIconPreviewButton className="bg-bluegray-600" />
      <div className="pl-4 bg-bluegray-600">
        {isFetching && isRefetching ? (
          <div className="bg-bluegray-600">
            <Loading
              loading={true}
              circularLoading={true}
              style={{
                paddingTop: '115px',
                paddingBottom: '115px',
              }}
            />
          </div>
        ) : (
          <AssetStuffingChart
            {...{
              data: chartData,
              extraData: chartIncorrectData,
              setRange,
              topixData,
            }}
          />
        )}
      </div>
      <div className="flex flex-row items-center relative z-10 -mt-6 mb-4 bg-bluegray-600">
        {UserBrokerUpdateTime}
      </div>
      {children}
    </>
  );
};

export type AssetHistoryItemsProps = {
  [key: string]: any;
  day: number;
  totalAssetAmount: number | null;
  diffTotalAssetAmount: number | null;
  diffTotalAssetRate: number | null;
  marginProfitOrLoss: number | null;
  totalProfitOrLoss: number | null;
  diffTotalProfitOrLoss: number | null;
  diffTotalProfitOrLossRate: number | null;
  stockAmount: number | null;
  marginAmount: number | null;
  roboAdvisorAmount: number | null;
  uSStockAmount: number | null;
  investmentTrustAmount: number | null;
  cashAmount: number | null;
  otherAmount: number | null;
  unlistedStockAmount: number | null;
  etfEtnAmount: number | null;
  foreignEtfEtnAmount: number | null;
  reitAmount: number | null;
  foreignReitAmount: number | null;
  bondAmount: number | null;
  foreignBondAmount: number | null;
  ebAmount: number | null;
  foreignEbAmount: number | null;
  realEstateAmount: number | null;
  incorrect: boolean;
  topix?: number | null;
};

export const AssetStuffing = ({
  children,
  asset,
  assetBundleType,
  UserBrokerUpdateTime,
  toPreview,
  backHome,
}: Omit<AssetStuffingProps, 'assets'>): React.ReactElement => {
  const { data, refetch, isFetching, isRefetching } = useQuery(
    'assetStuffingAssets',
    () => fetchAssets(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBundleType.key]);

  if (data === null || data === undefined)
    return assetBundleType.key === 0 ? (
      <LoadingTotalData />
    ) : (
      <LoadingGroupData />
    );

  const assets = data as AssetHistoryItemsProps[];

  return (
    <AssetStuffingInternal
      {...{
        children,
        asset,
        assets,
        UserBrokerUpdateTime,
        toPreview,
        backHome,
        isFetching,
        isRefetching,
      }}
    />
  );
};

const LoadingTotalData = () => {
  return (
    <>
      <div className="pl-4 pt-8 bg-bluegray-600">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '100px',
            paddingBottom: '100px',
          }}
        />
      </div>
      <div className="pt-8 px-4">
        <div className="text-lg font-bold">資産額の変動</div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
      </div>
      <ServiceList {...{ isTransition: false }} />
      <DividendItem />
      {!isIosApp() && (
        <>
          <div className="pt-8 px-4">
            <div className="text-lg font-bold">資産目標 今月の達成状況</div>
            <Loading
              loading={true}
              circularLoading={true}
              style={{
                paddingTop: '60px',
                paddingBottom: '60px',
              }}
            />
          </div>
        </>
      )}
      <ColumnList />
    </>
  );
};

const LoadingGroupData = () => {
  return (
    <>
      <div className="pl-4 mb-4 bg-bluegray-600">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '120px',
            paddingBottom: '120px',
          }}
        />
      </div>
      <div className="p-4 pb-6">
        <div className="border-b border-bluegray-300 px-4 pb-2">
          <div className="text-lg font-bold">登録口座</div>
        </div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
      </div>
    </>
  );
};

const fetchAssets = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;
  const res_ = await getAssetHistory(undefined, undefined, groupID, true);
  const res = res_.Items.map(item => {
    return {
      day: item.Day,
      totalAssetAmount: item.TotalAssetAmount,
      diffTotalAssetAmount: item.DiffTotalAssetAmount,
      diffTotalAssetRate: item.DiffTotalAssetRate,
      marginProfitOrLoss: item.MarginProfitOrLoss,
      totalProfitOrLoss: item.TotalProfitOrLoss,
      diffTotalProfitOrLoss: item.DiffTotalProfitOrLoss,
      diffTotalProfitOrLossRate: item.DiffTotalProfitOrLossRate,
      stockAmount: item.StockAmount,
      marginAmount: item.MarginAmount,
      roboAdvisorAmount: item.RoboAdvisorAmount,
      uSStockAmount: item.USStockAmount,
      investmentTrustAmount: item.InvestmentTrustAmount,
      unlistedStockAmount: item.UnlistedStockAmount,
      etfEtnAmount: item.EtfEtnAmount,
      foreignEtfEtnAmount: item.ForeignEtfEtnAmount,
      reitAmount: item.ReitAmount,
      foreignReitAmount: item.ForeignReitAmount,
      bondAmount: item.BondAmount,
      foreignBondAmount: item.ForeignBondAmount,
      ebAmount: item.EbAmount,
      foreignEbAmount: item.ForeignEbAmount,
      realEstateAmount: item.RealEstateAmount,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
      incorrect: item.Incorrect,
      topix: item.Topix ?? null,
    };
  });
  return res;
};
